import React, { useState, useMemo } from "react";
import {
  Boxes,
  Check,
  CheckCircle2,
  CircleDollarSign,
  Gauge,
  Save,
  TrendingUp,
} from "lucide-react";
import { Btn, Pill, Stat, Vazio, usarServidor } from "../ui/componentes";
import { brl } from "../ui/formato";

/* ══════════════════════════════════════════════════════════════════
   INTELIGÊNCIA DE ESTOQUE
   Quatro coisas que a oficina perde e não enxerga. Cada linha vem
   com a ação do lado — não "sua margem caiu", mas "reajuste para X".
   ══════════════════════════════════════════════════════════════════ */
const arredondarPreco = (v) =>
  v < 50 ? Math.ceil(v) : v < 200 ? Math.ceil(v / 5) * 5 - 0.1
  : v < 1000 ? Math.ceil(v / 10) * 10 - 0.1 : Math.ceil(v / 50) * 50;

function Inteligencia({ db, set, toast, acoes }) {
  const { executar } = usarServidor(toast);
  const MARGEM_ALVO = 0.45;
  const [aba, setAba] = useState("margem");

  const analise = useMemo(() => {
    const saidas = db.mov.filter((m) => m.tipo === "saida");
    const porPeca = {};
    for (const m of saidas) {
      const p = db.pecas.find((x) => x.id === m.peca);
      if (!p) continue;
      (porPeca[m.peca] ??= { qtd: 0, valor: 0, ultima: m.data, n: 0 });
      porPeca[m.peca].qtd += m.qtd;
      porPeca[m.peca].valor += m.qtd * p.custo;
      porPeca[m.peca].n += 1;
      if (m.data > porPeca[m.peca].ultima) porPeca[m.peca].ultima = m.data;
    }

    const margem = db.pecas
      .filter((p) => p.venda > 0 && p.custo > 0 && (p.venda - p.custo) / p.venda < MARGEM_ALVO - 0.05)
      .map((p) => {
        const atual = ((p.venda - p.custo) / p.venda) * 100;
        const sugerido = arredondarPreco(p.custo / (1 - MARGEM_ALVO));
        return { ...p, margemAtual: atual, sugerido, ganho: (sugerido - p.venda) * p.qtd,
                 prejuizo: p.venda < p.custo };
      }).sort((a, b) => b.ganho - a.ganho);

    const minimos = db.pecas.map((p) => {
      const c = porPeca[p.id];
      if (!c || c.n < 4) return null;
      const diario = c.qtd / 180;
      const sug = Math.ceil(diario * 7 * 1.3);
      return sug !== p.min && sug > 0
        ? { ...p, sugerido: sug, diario, confianca: c.n >= 8 ? "alta" : "media" } : null;
    }).filter(Boolean).sort((a, b) => b.diario - a.diario);

    const parado = db.pecas.filter((p) => p.qtd > 0 && !porPeca[p.id])
      .map((p) => ({ ...p, capital: p.qtd * p.custo }))
      .sort((a, b) => b.capital - a.capital);

    const total = Object.values(porPeca).reduce((s, x) => s + x.valor, 0);
    let acc = 0;
    const abc = db.pecas.map((p) => ({ ...p, giro: porPeca[p.id]?.valor ?? 0 }))
      .sort((a, b) => b.giro - a.giro)
      .map((p) => { acc += p.giro;
        return { ...p, classe: total === 0 ? "C" : acc / total <= 0.8 ? "A" : acc / total <= 0.95 ? "B" : "C" }; });

    return { margem, minimos, parado, abc,
      capitalParado: parado.reduce((s, p) => s + p.capital, 0),
      capitalTotal: db.pecas.reduce((s, p) => s + p.qtd * p.custo, 0),
      ganhoPotencial: margem.reduce((s, p) => s + Math.max(0, p.ganho), 0) };
  }, [db]);

  // O servidor recalcula e arredonda: R$ 219,90, não R$ 218,43. Preço com
  // centavo de fórmula faz o dono corrigir tudo à mão e largar a ferramenta.
  const reajustar = (ids) => executar(
    () => acoes.reajustarPrecos(ids.map((pecaId) => ({ pecaId }))),
    (r) => toast("Preços reajustados",
      `${r.ajustadas} peça(s) voltaram para a margem alvo · aumento médio de ${r.aumentoMedio}%.`),
    "Não deu para reajustar");

  const aplicarMinimos = () => executar(
    () => acoes.aplicarMinimos(analise.minimos.map((m) => m.id)),
    (r) => toast("Mínimos ajustados", `${r.aplicados} peça(s) agora usam o consumo real.`),
    "Não deu para aplicar");

  const abas = [["margem", "Margem", analise.margem.length],
                ["minimo", "Mínimo", analise.minimos.length],
                ["parado", "Parado", analise.parado.length],
                ["abc", "Curva ABC", null]];

  return (
    <div className="grid gap-4">
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))" }}>
        <Stat label="Ganho deixado na mesa" value={brl(analise.ganhoPotencial)} tone="signal"
          icon={TrendingUp} sub={`${analise.margem.length} peça(s) abaixo da margem alvo`} />
        <Stat label="Capital dormindo" value={brl(analise.capitalParado)} icon={Boxes}
          sub={`${((analise.capitalParado / (analise.capitalTotal || 1)) * 100).toFixed(0)}% do estoque sem giro`} />
        <Stat label="Peças classe A" value={analise.abc.filter((p) => p.classe === "A").length}
          icon={CircleDollarSign} sub="respondem por 80% do movimento" />
        <Stat label="Mínimos a corrigir" value={analise.minimos.length} icon={Gauge}
          sub="calculados pelo consumo real" />
      </div>

      <div className="flex gap-1 scroll" style={{ overflowX: "auto", borderBottom: "1px solid var(--line)" }}>
        {abas.map(([k, l, n]) => (
          <button key={k} className={`tab ${aba === k ? "on" : ""}`} onClick={() => setAba(k)}>
            {l} {n != null && <span className="mono t-mut2">{n}</span>}
          </button>
        ))}
      </div>

      {aba === "margem" && (
        <div className="card" style={{ overflow: "hidden" }}>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" style={{ borderBottom: "1px solid var(--line)" }}>
            <div>
              <div className="dsp" style={{ fontSize: 15 }}>Margem que derreteu</div>
              <div className="text-xs t-mut mt-0.5">O custo subiu, o preço ficou parado. Ninguém percebe porque o preço não mudou.</div>
            </div>
            {analise.margem.length > 0 && (
              <Btn variant="s" size="xs" icon={Check} onClick={() => reajustar(analise.margem.map((m) => m.id))}>
                Reajustar todas
              </Btn>
            )}
          </div>
          {analise.margem.length === 0
            ? <Vazio icon={CheckCircle2} titulo="Todas dentro da margem" msg="Nenhuma peça está sendo vendida abaixo do alvo de 45%." />
            : <div style={{ overflowX: "auto" }}>
                <table className="tbl">
                  <thead><tr><th>Peça</th><th className="num">Custo</th><th className="num">Venda hoje</th><th className="num">Margem</th><th className="num">Sugerido</th><th className="num">Ganho no estoque</th><th></th></tr></thead>
                  <tbody>
                    {analise.margem.map((p) => (
                      <tr key={p.id}>
                        <td><span className="text-sm font-medium">{p.nome}</span>
                          <span className="block text-xs t-mut2 mono">{p.cod} · {p.qtd} {p.un} em estoque</span></td>
                        <td className="num text-sm t-mut">{brl(p.custo)}</td>
                        <td className="num text-sm">{brl(p.venda)}</td>
                        <td className="num text-sm">
                          {p.prejuizo ? <Pill t="crit">abaixo do custo</Pill>
                            : <span className={p.margemAtual < 25 ? "t-crit font-semibold" : "t-warn font-semibold"}>{p.margemAtual.toFixed(0)}%</span>}
                        </td>
                        <td className="num text-sm font-semibold t-ok">{brl(p.sugerido)}</td>
                        <td className="num text-sm">{brl(Math.max(0, p.ganho))}</td>
                        <td><Btn variant="o" size="xs" onClick={() => reajustar([p.id])}>Aplicar</Btn></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>}
        </div>
      )}

      {aba === "minimo" && (
        <div className="card" style={{ overflow: "hidden" }}>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" style={{ borderBottom: "1px solid var(--line)" }}>
            <div><div className="dsp" style={{ fontSize: 15 }}>Mínimo calculado, não chutado</div>
              <div className="text-xs t-mut mt-0.5">Consumo médio diário × prazo do fornecedor, com folga.</div></div>
            {analise.minimos.length > 0 && <Btn variant="p" size="xs" icon={Save} onClick={aplicarMinimos}>Aplicar todos</Btn>}
          </div>
          {analise.minimos.length === 0
            ? <Vazio icon={Gauge} titulo="Sem consumo suficiente" msg="Ainda não há saídas bastantes para estimar o mínimo com confiança." />
            : <table className="tbl">
                <thead><tr><th>Peça</th><th className="num">Consumo/dia</th><th className="num">Mínimo hoje</th><th className="num">Sugerido</th><th>Confiança</th></tr></thead>
                <tbody>
                  {analise.minimos.map((p) => (
                    <tr key={p.id}>
                      <td className="text-sm font-medium">{p.nome}</td>
                      <td className="num text-sm mono">{p.diario.toFixed(2)}</td>
                      <td className="num text-sm t-mut">{p.min}</td>
                      <td className="num text-sm font-semibold">{p.sugerido} {p.un}</td>
                      <td><Pill t={p.confianca === "alta" ? "ok" : "warn"}>{p.confianca}</Pill></td>
                    </tr>
                  ))}
                </tbody>
              </table>}
        </div>
      )}

      {aba === "parado" && (
        <div className="card" style={{ overflow: "hidden" }}>
          <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--line)" }}>
            <div className="dsp" style={{ fontSize: 15 }}>Capital dormindo na prateleira</div>
            <div className="text-xs t-mut mt-0.5">Peça sem saída registrada. Vale o que alguém pagar por ela — e a prateleira também custa.</div>
          </div>
          {analise.parado.length === 0
            ? <Vazio icon={CheckCircle2} titulo="Tudo girando" msg="Nenhuma peça encalhada no período." />
            : <table className="tbl">
                <thead><tr><th>Peça</th><th>Local</th><th className="num">Qtd</th><th className="num">Custo unit.</th><th className="num">Capital parado</th></tr></thead>
                <tbody>
                  {analise.parado.map((p) => (
                    <tr key={p.id}>
                      <td className="text-sm font-medium">{p.nome}</td>
                      <td className="mono text-xs t-mut">{p.local}</td>
                      <td className="num text-sm">{p.qtd}</td>
                      <td className="num text-sm t-mut">{brl(p.custo)}</td>
                      <td className="num text-sm font-semibold t-signal">{brl(p.capital)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>}
        </div>
      )}

      {aba === "abc" && (
        <div className="card" style={{ overflow: "hidden" }}>
          <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--line)" }}>
            <div className="dsp" style={{ fontSize: 15 }}>Curva ABC</div>
            <div className="text-xs t-mut mt-0.5">Poucas peças respondem pela maior parte do dinheiro. Tratar pastilha e presilha igual erra nas duas.</div>
          </div>
          <table className="tbl">
            <thead><tr><th>Classe</th><th>Peça</th><th className="num">Movimentado</th><th className="num">Em estoque</th></tr></thead>
            <tbody>
              {analise.abc.slice(0, 12).map((p) => (
                <tr key={p.id}>
                  <td><Pill t={p.classe === "A" ? "ok" : p.classe === "B" ? "info" : "mut"}>{p.classe}</Pill></td>
                  <td className="text-sm font-medium">{p.nome}</td>
                  <td className="num text-sm">{brl(p.giro)}</td>
                  <td className="num text-sm t-mut">{p.qtd} {p.un}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export { arredondarPreco, Inteligencia };
